import '@/pages/doctor/shared/doctorSurface.css';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { Brand, Chip, DataTable, EmptyState, Icon, TopBar, type TableRow } from '@/components/kit';
import type { TranscriptSegment } from '@/hooks/useLiveRecording';
import { useRecordingSessionStore } from '@/stores/useRecordingSessionStore';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { uploadAttachment, attachmentsKeys } from '@/hooks/useAttachments';
import { useDoctorPatientFile } from '@/pages/doctor/file/usePatientFile';
import { useVisitFlows, clock } from '@/pages/doctor/shared/useFlows';
import { shortName } from '@/pages/doctor/shared/format';

/**
 * Doctor · The new visit — `maie-desktop-doctor/g_encn.dc.html` (f10n).
 *
 * A RE-SKIN, NOT A REBUILD. Everything about capture — acquiring the mic, the
 * chunk cadence, the offline queue, the completion intent that has to be
 * persisted before the first await, the synth job registration — stays in
 * RecordingDock and `useLiveRecording`, which is where the hard-won rules live.
 * This component is handed the same values the existing stage body gets and
 * draws the canvas's version of them. Anything that could lose a doctor's
 * consult is deliberately not reimplemented here.
 *
 * STOPPING IS WHAT WRITES THE NOTE, and the screen says so in the canvas's own
 * words. Until then the visit has no S, no O, no A and no P.
 *
 * THE CONSENT LINE IS NOT A CHECKBOX. "Starting the capture IS the consent" —
 * so it is reported, not asked.
 *
 * THE RAIL STAYS. The board draws the recorder inside the desk, rail and all,
 * so the doctor can leave it for the file and come back (g_fup). Any door
 * taken from the rail minimizes the stage to the strip — the capture never
 * pauses for it. (The dock's overlay is full-screen; doctorSurface.css starts
 * it after the rail — see the lane report for the shell-side version.)
 *
 * THE VOICE PATTERN IS THE ONLY AUDIO VISUAL (Brand §10) — two layers of the
 * brand's pattern, not a level meter.
 */

export interface CollectedRow {
  what: string;
  detail: string;
  source: string;
  state: { label: string; kind: 'danger' | 'success' | 'warn' | 'outline' };
}

interface Props {
  /** Whose consult this is — the stage covers the shell, so it wears the head. */
  patientName: string;
  /** Safety carried from the file — "riding along", never re-asked in the room. */
  allergies: string[];
  conditions: string[];
  /** Seconds, as the dock tracks them — the stage does its own formatting. */
  elapsed: number;
  isPaused: boolean;
  connected: boolean;
  ready: boolean;
  pendingChunks: number;
  segments: TranscriptSegment[];
  /** The analyser's levels — no longer drawn (the pattern is the visual). */
  bars: number[];
  /** Rows for "everything this visit is collecting" — real sources only. */
  collecting: CollectedRow[];
  firstVisit: boolean;
  onTogglePause: () => void;
  onComplete: () => void;
  onAbandon: () => void;
  onBack: () => void;
}

/** mm:ss from the elapsed seconds the dock already formats. */
function stamp(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const isToday = (iso?: string | null) => {
  if (!iso) return false;
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString();
};

export function MaieConsultStage({
  patientName, allergies, conditions, elapsed, isPaused, connected, ready,
  pendingChunks, segments, collecting, firstVisit,
  onTogglePause, onComplete, onAbandon, onBack,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const session = useRecordingSessionStore((s) => s.session);
  const patientId = session?.patientId ?? '';
  const f = useDoctorPatientFile(patientId);
  const flows = useVisitFlows(null, patientId || null);
  const { person } = useShellIdentity();
  const camRef = useRef<HTMLInputElement>(null);

  /* A DOOR TAKEN FROM THE RAIL minimizes the stage — the route changed under
     it, and a recorder that stays on top would make the rail look dead. */
  const firstPath = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== firstPath.current) onBack();
  }, [location.pathname, onBack]);

  const leaveTo = (path: string) => { onBack(); navigate(path); };

  const name = f.patient ? shortName(f.patient, f.name) : patientName;
  const title = [name || 'Consult in progress', f.ageSex].filter(Boolean).join(' · ');
  const started = session?.startedAt ? clock(new Date(session.startedAt).toISOString()) : '';
  const episode = f.episode;
  /* FIRST PRESENTATION means nothing signed before it. The dock's own guess
     reads `patients.lastVisitDate`, which nothing writes (backlog §16), so it
     called every visit a first one; the file's visits are the fact. */
  const first = f.patient ? !f.visits.some((v) => v.status === 'signed' || v.signedAt) : firstVisit;

  /* The canvas stamps each line with when it was heard. Segments carry
     `receivedAt` wall-clock, so the offset is measured from the first one. */
  const heard = useMemo(() => {
    if (!segments.length) return [];
    const t0 = segments[0].receivedAt;
    return segments.map((s) => ({
      id: s.id,
      at: stamp((s.receivedAt - t0) / 1000),
      text: s.text,
      pending: s.pending,
      /* The board tags each line S or O. That tagging is the scribe's job and
         it does not happen until synth runs, so a tag here would be invented.
         The speaker label IS real when diarization is on. */
      who: s.speaker,
    }));
  }, [segments]);

  /* EVERYTHING THIS VISIT IS COLLECTING — the room (from the dock), plus what
     the file and the flows already hold for today: the door vitals with who
     took them, a flow running beside the visit, and the scanner. */
  const vitalsToday = useMemo(() => {
    const bp = f.measures.find((m) => m.key === 'bp');
    const hr = f.measures.find((m) => m.key === 'hr');
    const latest = [bp, hr].map((m) => m?.readings[0]).filter((r) => r && isToday(r.at));
    if (!latest.length) return null;
    const r0 = latest[0]!;
    return {
      what: [bp?.readings[0] && isToday(bp.readings[0].at) ? `BP ${bp.readings[0].value}` : null,
        hr?.readings[0] && isToday(hr.readings[0].at) ? `HR ${hr.readings[0].value.replace(/ bpm$/, '')}` : null]
        .filter(Boolean).join(' · '),
      /* Who took it and when — the taker's role is not on the reading, so it is not printed. */
      source: [r0.by, clock(r0.at)].filter(Boolean).join(' · '),
    };
  }, [f.measures]);
  const running = flows.data.filter((x) => x.state === 'running'
    && (!x.medicalRecordId || x.medicalRecordId === session?.medicalRecordId));

  const rowsData: CollectedRow[] = [
    ...collecting.map((c, i) => (i === 0 && started
      ? { ...c, detail: c.detail.replace('since the capture started', `since ${started}`) } : c)),
    ...(vitalsToday ? [{
      what: vitalsToday.what,
      detail: `taken at the door, before you saw ${pronounOf(f.patient, 'him')}`,
      source: vitalsToday.source,
      state: { label: 'attached', kind: 'success' as const },
    }] : []),
    ...running.map((x) => ({
      what: x.name,
      detail: `step ${Math.min(x.currentStep + 1, x.steps.length)} of ${x.steps.length} — it runs beside the visit, not inside it`,
      source: `flow · started ${clock(x.startedAt)}`,
      state: { label: `${Math.min(x.currentStep + 1, x.steps.length)}/${x.steps.length}`, kind: 'warn' as const },
    })),
    {
      what: 'Anything you scan',
      /* The board's line is true only once the capture has a note to land
         in (`medicalRecordId`); before that the camera files to the chart,
         dated today, and the line says so — see "Disputed findings". */
      detail: session?.medicalRecordId
        ? 'the camera in the header drops it straight into this visit'
        : `the camera in the header files it to ${pronounOf(f.patient, 'his')} chart, dated today`,
      source: 'desk scanner · phone',
      state: { label: 'ready', kind: 'outline' },
    },
  ];

  const rows: TableRow[] = rowsData.map((c, i) => ({
    id: String(i),
    cells: {
      /* The board's cell exactly: an inline title on the cell's own line box,
         then the detail — a wrapping <div> set the title on a 20px line and
         pulled the detail 5px up (rows 63px against the board's 67). */
      what: (
        <>
          <span className="t-strong">{c.what}</span>
          <p className="t-cap mut">{c.detail}</p>
        </>
      ),
      where: <span className="mut">{c.source}</span>,
      state: <Chip kind={c.state.kind} small>{c.state.label}</Chip>,
    },
  }));

  const scan = async (file: File | undefined) => {
    if (!file || !patientId) return;
    try {
      await uploadAttachment({ patientId, file, medicalRecordId: session?.medicalRecordId });
      void qc.invalidateQueries({ queryKey: attachmentsKeys.list(patientId) });
      toast.success('Filed to the chart', { description: file.name });
    } catch {
      toast.error('The scan did not upload.');
    }
  };

  const narrow = useNarrowViewport();
  const safety = [...allergies.map((a) => `${a} allergy`), ...conditions];

  return (
    <div className="maie desk dr-stage dr-surface" style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* Its own `.maie` container, so the top bar's container query measures
          the content column (as in the shell), not the stage with its pane. */}
      <div className="maie desk" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {narrow ? (
          <div className="topstrip">
            <button type="button" className="ts-btn" aria-label="Back to patients" onClick={() => leaveTo('/patients')}>
              <Icon name="chevron-left" size="lg" />
            </button>
            <p className="ts-title grow" style={{ minWidth: 0 }}>{name || 'Consult'} · {first ? 'New visit' : 'Follow-up'}</p>
            {first || !episode ? <Chip kind="soft" small>New episode</Chip> : null}
          </div>
        ) : (
        <TopBar
          title={title}
          sub={
            <>
              <span className="w6 brand" role="link" tabIndex={0} style={{ cursor: 'pointer' }}
                onClick={() => leaveTo('/patients')}
                onKeyDown={(e) => e.key === 'Enter' && leaveTo('/patients')}>‹ Patients</span>
              {' '}<span className="mut">▸</span>{' '}
              {first ? 'New visit · first presentation' : 'Follow-up'}{' '}
              {first || !episode ? <><Chip kind="soft" small>New episode</Chip>{' '}</> : (
                <><Chip kind="warn" small>{episode.name} — OPEN</Chip>{' '}</>
              )}
              {allergies.map((a) => (
                <span key={a}><Chip kind="danger" small>{a}</Chip>{' '}</span>
              ))}
              {conditions.slice(0, 2).map((c) => (
                <span key={c}><Chip kind="outline" small>{c}</Chip>{' '}</span>
              ))}
            </>
          }
          search="In this file — a result, a drug, a document"
          onSearch={() => patientId && leaveTo(`/patients/${patientId}`)}
          onBell={() => leaveTo('/notifications')}
          extra={
            <>
              <button type="button" className="iconbtn" aria-label="Scan into this visit"
                title="Scan or pick a file" onClick={() => camRef.current?.click()}>
                <Icon name="camera" />
              </button>
              <input ref={camRef} type="file" accept="image/*,application/pdf" hidden
                onChange={(e) => { void scan(e.target.files?.[0]); e.target.value = ''; }} />
            </>
          }
        />
        )}

        <div className="dcontent" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div className="flex" style={{ gap: 10, margin: '16px 0 6px' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
              background: isPaused ? 'var(--ink-400)' : 'var(--danger-600)' }} />
            <p className="t-heading grow" style={{ color: isPaused ? 'var(--ink-600)' : 'var(--danger-600)' }}>
              {isPaused ? 'Paused' : 'Recording'} — {first ? 'first presentation' : 'follow-up'}
            </p>
          </div>

          {/* Reported, not asked — see the header. */}
          <p className="t-sec mut mb16">
            Ambient · verbal consent taken{person?.name ? ` by ${person.name}` : ''}{started ? ` · ${started}` : ''}
            {narrow ? '' : ' — starting the capture IS the consent; no form, no checkbox.'}
          </p>

          <div className="card" style={{ textAlign: 'center', padding: '24px 20px', borderColor: 'var(--brand-300)' }}>
            <div style={{ position: 'relative', maxWidth: 320, margin: '0 auto', opacity: isPaused ? 0.4 : 1 }}>
              <Brand mark="pattern" style={{ display: 'block', width: '100%', color: 'var(--brand-300)', opacity: 0.55,
                position: 'absolute', left: 0, top: '7%', transform: 'scale(1.04)' }} />
              <Brand mark="pattern" style={{ display: 'block', width: '100%', color: 'var(--brand-500)', position: 'relative' }} />
            </div>

            <p style={{ fontSize: 44, lineHeight: '52px', fontWeight: 600, marginTop: 18 }}>{stamp(elapsed)}</p>
            {narrow ? null : (
              <p className="t-sec mut">
                elapsed — the strip runs until you stop it, and it never stops on its own
              </p>
            )}

            <div className="flex" style={{ justifyContent: 'center', gap: 12, marginTop: 18 }}>
              <button type="button" className="btn btn-outline"
                style={{ minHeight: 48, padding: '0 26px', fontSize: 15 }} onClick={onTogglePause}>
                {isPaused ? '▶ Resume' : '⏸ Pause'}
              </button>
              <button type="button" className={narrow ? 'btn' : 'btn btn-primary'}
                style={{ minHeight: 48, padding: '0 26px', fontSize: 15,
                  ...(narrow ? { background: 'var(--danger-100)', color: 'var(--danger-600)', border: 0 } : null) }}
                onClick={onComplete}>
                {narrow ? 'Stop' : '■ Stop — generate the note'}
              </button>
            </div>

            <p className="t-cap mut mt12" style={narrow ? { display: 'none' } : undefined}>
              Stopping is what writes the note. Until then this visit has no S, no O, no A and no P — and
              nothing to sign.{' '}
              <span className="lnk" role="link" tabIndex={0} onClick={onAbandon}
                onKeyDown={(e) => e.key === 'Enter' && onAbandon()}>
                Discard this capture
              </span>
            </p>

            {/* The capture's health, only when there is something to say. */}
            {!connected || !ready || pendingChunks > 0 ? (
              <p className="t-cap amb mt8">
                {!connected
                  ? 'Offline — audio is saved on this device and will be processed when the network returns.'
                  : !ready
                    ? 'Acquiring the microphone…'
                    : `${pendingChunks} chunk${pendingChunks === 1 ? '' : 's'} still uploading.`}
              </p>
            ) : null}
          </div>

          {narrow ? null : <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>
            EVERYTHING THIS VISIT IS COLLECTING WHILE YOU TALK
          </p>}
          {narrow ? null : <DataTable
            columns={[
              { key: 'what', label: 'What the visit is capturing', width: 46 },
              { key: 'where', label: 'Where it comes from', width: 32 },
              { key: 'state', label: '', width: 22 },
            ]}
            rows={rows}
            caption="Collecting"
            className="dr-static"
            empty={<EmptyState title="Only the room" sub="Nothing else is attached to this visit yet." />}
          />}
        </div>
      </div>

      {/* THE PANE IS THE LIVE PICK-UP, and its head is the disclaimer. */}
      {narrow ? null : <div className="maie desk kit-pane-dock">
        <aside className="pane">
          <div className="pane-head">
            <div>
              <p className="t-ovl mut">MAIE · LISTENING</p>
              <p className="t-title" style={{ marginTop: 2 }}>Picking it up as {pronounOf(f.patient)} says it</p>
              <p className="t-sec mut">
                none of this is a note — the note does not exist until you stop
              </p>
            </div>
          </div>
          <div className="pane-body">
            <p className="t-ovl mut" style={{ margin: '0 0 6px' }}>HEARD SO FAR — {stamp(elapsed)}</p>
            {heard.length ? (
              heard.map((h) => (
                <div key={h.id} className="card card-flat" style={{ padding: '10px 14px', marginBottom: 8 }}>
                  <div className="flex" style={{ gap: 10, alignItems: 'flex-start' }}>
                    <span className="t-cap mut" style={{ flexShrink: 0, minWidth: 42 }}>{h.at}</span>
                    <p className={`t-sec grow ${h.pending ? 'mut' : 'sec'}`}>
                      {h.text.split(/(\[unclear\])/i).map((part, i) =>
                        /^\[unclear\]$/i.test(part) ? <span key={i} className="amb w6">{part}</span> : part)}
                    </p>
                    {h.who ? <Chip kind="soft" small>{h.who}</Chip> : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="card card-flat" style={{ padding: '10px 14px', marginBottom: 8 }}>
                <p className="t-sec mut">
                  {connected ? 'Nothing picked up yet — lines appear here as they are heard.' : 'Listening — text appears once the network is back.'}
                </p>
              </div>
            )}

            {/* CARRIED FROM THE FILE, not from the room. */}
            <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>ALREADY ON FILE — RIDING ALONG</p>
            <div className="card">
              <p className="t-sec sec">{safety.length ? safety.join(' · ') : 'Nothing on file — no allergies or conditions recorded.'}</p>
              <p className="t-cap mut mt8">
                carried from {pronounOf(f.patient, 'his')} file, not from the room — the capture never re-asks what
                the record already knows
              </p>
            </div>
            <div className="card" style={{ borderColor: 'var(--brand-300)' }}>
              <p className="t-sec" style={{ color: 'var(--brand-600)' }}>
                Stop, and MAIE drafts S · O · A · P from exactly this. Every line opens editable, nothing
                is signed, and what you never said is not in there.
              </p>
            </div>
          </div>
        </aside>
      </div>}
    </div>
  );
}

/** "he" / "she" / "they" — and the possessive — for the lines about the patient. */
function pronounOf(p: { gender?: string | null } | null | undefined, form: 'he' | 'his' | 'him' = 'he'): string {
  const g = String(p?.gender ?? '').toLowerCase();
  const set = g.startsWith('f') ? { he: 'she', his: 'her', him: 'her' }
    : g.startsWith('m') ? { he: 'he', his: 'his', him: 'him' }
    : { he: 'they', his: 'their', him: 'them' };
  return set[form];
}
